/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'
import {
  capitalize,
  flatMap,
  groupBy,
  intersection,
  intersectionWith,
  isEqual,
  maxBy,
  sumBy,
  sortBy,
} from 'lodash'

import { EuiBadge, EuiToolTip } from '@elastic/eui'

import type {
  ElasticsearchClusterTopologyElement,
  InstanceConfiguration,
  AppSearchTopologyElement,
} from '@modules/cloud-api/v1/types'
import type {
  AnyTopologyElement,
  ArchitectureBreakdownItem,
  ArchitectureSummary,
  BasePrice,
  BillingSubscriptionLevel,
  EsNodeType,
  InstanceType,
  NodeConfiguration,
  NodeRole,
  NodeType,
  RegionId,
  SliderInstanceType,
  VersionNumber,
  ZoneSummary,
  ZoneSummaryCounts,
  ZoneSummaryInstance,
  ZoneSummaryKey,
} from '@modules/ui-types'

import {
  isFrozen,
  isCold,
  isWarm,
  isHot,
  isDedicatedMaster,
  isDedicatedML,
  isMaster,
  getUnhiddenNodeRoles,
  getNodeRoles,
} from '@/lib/stackDeployments/selectors/nodeRoles'
import { isValidSliderNodeType, isSliderInstanceType } from '@/lib/sliders/support'
import { sortNodeTypes } from '@/lib/sliders/sorting'
import { getSliderPrettyName } from '@/lib/sliders/messages'

import {
  blobStorageMultiplier,
  getNumber,
} from '../../components/Topology/DeploymentTemplates/components/DeploymentInfrastructure/TopologyElement/helpers'
import { getConfigForKey } from '../../store'
import { formatCenticent } from '../money'
import prettySize from '../prettySize'
import RatioLabel from '../../components/Topology/DeploymentTemplates/components/RatioLabel'

import { getTiebreakerMaster, hasTiebreakerMaster } from './masters'
import { getNodeTypeForArchitectureSummary } from './nodeTypes'
import { faultToleranceZones } from './faultTolerance'
import { messages, dedicatedNodeTypeMessages } from './messages'
import { getInstanceCount, getInstanceSize, getSize, isEnabledConfiguration } from './conversion'

import type { IntlShape } from 'react-intl'
import type { ReactNode } from 'react'

export type SummaryItems = Array<{
  title: ReactNode
  description: ReactNode
  id?: string
}>

export type ConfigurationItem = {
  title: string
  type: string
  titleClass?: string
  descriptionClass?: string
  description?: ReactNode
  summaryItems?: SummaryItems
}

const topEsTypes: EsNodeType[] = [`data`, `master`, `ml`, `ingest`]

const dedicatedNodeTypes: EsNodeType[] = [`master`, `ingest`]

function deriveNodeType(
  nodeConfiguration: AnyTopologyElement | null,
  instance_type: InstanceType | string,
) {
  if (!nodeConfiguration) {
    return instance_type // sanity
  }

  if (instance_type !== `elasticsearch`) {
    return instance_type
  }

  const activeNodeTypes: NodeRole[] = getNodeRoles({
    topologyElement: nodeConfiguration,
  })

  // In the unlikely event that we can't work out the best Elasticsearch node type,
  // default it to data
  if (activeNodeTypes.length === 0) {
    return `data`
  }

  const bestEsTypes = intersectionWith(topEsTypes, activeNodeTypes, (type, role) =>
    role.startsWith(type),
  )

  return bestEsTypes.length > 0 ? bestEsTypes[0] : activeNodeTypes[0]
}

export function getArchitectureDescription({
  nodeConfigurations,
  instanceConfigurations = [],
  version,
}: {
  nodeConfigurations: NodeConfiguration[]
  instanceConfigurations?: InstanceConfiguration[] | null
  version: VersionNumber | undefined
}): ArchitectureSummary {
  const zones = [] as ZoneSummary[]
  const instanceKey = {}
  const dataNodeTypes = new Set()

  for (const zoneCount of faultToleranceZones) {
    const zone = [] as ZoneSummary

    for (const nodeConfiguration of nodeConfigurations) {
      const {
        instance_configuration_id,
        zone_count = 1,
        size,
        memory_per_node,
        node_count_per_zone,
        id,
        autoscaling_min,
      } = nodeConfiguration as ElasticsearchClusterTopologyElement

      const enabled = isEnabledConfiguration(nodeConfiguration)

      if (!enabled) {
        continue
      }

      const instanceConfiguration = instanceConfigurations?.find(
        (each) => each.id === instance_configuration_id,
      )

      if (!instanceConfiguration) {
        continue
      }

      const isElasticsearch = instanceConfiguration.instance_type === `elasticsearch`

      if (zoneCount > zone_count && (zoneCount !== 1 || isElasticsearch)) {
        continue
      }

      const hasTieBreaker = hasTiebreakerMaster(nodeConfigurations, instanceConfigurations)
      const type = getNodeTypeForArchitectureSummary(
        nodeConfiguration,
        instanceConfiguration,
        hasTieBreaker,
      )

      const { resource, sizes, default_size } = instanceConfiguration.discrete_sizes
      let sizeValue = size ? size.value || default_size : undefined

      if (isDedicatedML({ topologyElement: nodeConfiguration }) && sizeValue === undefined) {
        // In the case of ML it doesn't have a size object defined when we set the min/max values. Frozen tier and the other data tiers
        // and even the stateless components, all have a `size-per-zone` label but ML has a `min-size` per zone label. This of course only
        // occurs if autoscaling is turned on (deployment level or just ML). If it is not, then ML just has a size object like all other tiers.
        // ML does acquire a size once it has been set to a min-size greater than 0 and the node has actually been fired up.
        sizeValue = autoscaling_min?.value
      }

      const numberOfInstances = getInstanceCount({
        size: sizeValue,
        sizes,
        exactInstanceCount: node_count_per_zone,
      })

      const instanceSize = getInstanceSize({
        size: sizeValue,
        sizes,
        exactSize: memory_per_node,
      })

      const sliderInstanceType = instanceConfiguration.instance_type

      const sliderNodeTypes: NodeType[] | undefined = getNodeRoles({
        topologyElement: nodeConfiguration,
        version,
      }).filter((sliderNodeType) =>
        isValidSliderNodeType({ sliderInstanceType, sliderNodeType }),
      ) as NodeType[]

      const instance: ZoneSummaryInstance = {
        id: instanceConfiguration.id || instanceConfiguration.name,
        name: instanceConfiguration.name,
        icConfigVersion: instanceConfiguration.config_version,
        type,
        topologyId: id,
        sliderInstanceType,
        sliderNodeType: sliderNodeTypes,
        size: <RatioLabel resource={resource || `memory`} size={instanceSize!} />,
        totalSize: instanceSize ? instanceSize * numberOfInstances : instanceSize,
        cpuMultiplier: instanceConfiguration.cpu_multiplier,
        storageMultiplier: instanceConfiguration.storage_multiplier,
      }

      if (type.startsWith('data')) {
        const storageSize = getSize({
          resource: `storage`,
          size,
          exactSize: memory_per_node,
          exactInstanceCount: node_count_per_zone,
          instanceConfiguration,
        })
        instance.storage = (
          <RatioLabel key='arch-viz-storage' resource='storage' size={storageSize} />
        )
        instance.storagePerInstance = (
          <RatioLabel
            key='arch-viz-storage-per-instance'
            resource='storage'
            size={storageSize / numberOfInstances}
          />
        )

        dataNodeTypes.add(instance.id)
        instance.index = Array.from(dataNodeTypes).indexOf(instance.id)
      }

      for (let i = 0; i < numberOfInstances; i++) {
        zone.push(instance)
      }

      if (instanceKey[type] === undefined) {
        instanceKey[type] = {}
      }

      instanceKey[type][instance_configuration_id] = instance
    }

    if (zone.length > 0) {
      const sortedZone = zone.sort((a, b) => sortNodeTypes(a.type, b.type))
      zones.push(sortedZone)
    }
  }

  const sortedKeys = Object.keys(instanceKey).sort(sortNodeTypes)
  const keys = flatMap(sortedKeys, (type) =>
    Object.keys(instanceKey[type]).map((key) => {
      const counts = getInstanceAndZoneCounts(zones, key)
      return [instanceKey[type][key], counts]
    }),
  ) as Array<[ZoneSummaryInstance, ZoneSummaryCounts]>

  return { zones, keys }
}

export function getInstanceAndZoneCounts(
  zones: ZoneSummaryInstance[][],
  key: string,
): ZoneSummaryCounts {
  return zones.reduce(
    (accum, zone) => {
      const matches = zone.filter(({ id }) => key === id)

      if (matches.length) {
        accum.zones += 1
        accum.instances += matches.length
      }

      return accum
    },
    { zones: 0, instances: 0 },
  )
}

export function getArchitectureSummaryItems({
  intl: { formatMessage },
  instanceConfigurations,
  nodeConfigurations,
  deploymentName,
  deploymentVersion,
}: {
  intl: IntlShape
  instanceConfigurations: InstanceConfiguration[]
  nodeConfigurations: NodeConfiguration[]
  deploymentName?: string
  deploymentVersion?: string
}): ConfigurationItem[] {
  const nodeTypes = sumNodeTypes(nodeConfigurations, instanceConfigurations)

  const subtotalMemory = sumMemory({ instanceConfigurations, nodeConfigurations })
  const systemAddedMemory = sumSystemAddedMemory({ instanceConfigurations, nodeConfigurations })
  const totalMemory = subtotalMemory + systemAddedMemory

  // We only want to show storage for es data nodes
  const esDataNodes = getEsDataNodes({ instanceConfigurations, nodeConfigurations })
  const totalStorage = sumStorage({
    instanceConfigurations,
    nodeConfigurations: esDataNodes,
  })

  const configurationItems: ConfigurationItem[] = []

  if (deploymentName) {
    configurationItems.push({
      title: formatMessage(messages.summaryName),
      type: `title`,
      description: (
        <EuiToolTip position='left' content={deploymentName} display='block'>
          <span>{deploymentName}</span>
        </EuiToolTip>
      ),
    })
  }

  if (deploymentVersion) {
    configurationItems.push({
      title: formatMessage(messages.summaryVersion),
      type: `title`,
      description: (
        <EuiBadge className='architecture-summary--version'>v{deploymentVersion}</EuiBadge>
      ),
    })
  }

  Object.keys(nodeTypes)
    .sort((a, b) => sortNodeTypes(a, b))
    .map((node) => {
      const instanceTitle = isSliderInstanceType(node)
        ? formatMessage(
            getSliderPrettyName({ sliderInstanceType: node, version: deploymentVersion }),
          )
        : node
      const dedicatedNodeItems = dedicatedNodeTypes
        .filter((dedicatedNodeType) => nodeTypes[node][dedicatedNodeType])
        .map((dedicatedNodeType) => ({
          id: dedicatedNodeType,
          title: formatMessage(dedicatedNodeTypeMessages[dedicatedNodeType]),
          description: prettySize(nodeTypes[node][dedicatedNodeType]),
        }))

      configurationItems.push({
        title: instanceTitle,
        type: node,
        summaryItems: [
          ...(node === `elasticsearch` ? renderElasticsearchNodeSummary(nodeTypes[node]) : []),
          ...(nodeTypes[node].memory
            ? [
                {
                  id: `memory`,
                  title: formatMessage(messages.summaryMemory),
                  description: prettySize(nodeTypes[node].memory),
                },
              ]
            : []),
          ...dedicatedNodeItems,
        ],
      })
    })

  configurationItems.push({
    title: formatMessage(messages.total),
    type: `total`,
    titleClass: `total`,
    summaryItems: [
      {
        id: `storage`,
        title: formatMessage(messages.summaryTotalStorage),
        description: prettySize(totalStorage),
      },
      {
        id: `memory`,
        title: formatMessage(messages.summaryTotalMemory),
        description: prettySize(totalMemory),
      },
    ],
  })
  return configurationItems

  function renderElasticsearchNodeSummary(node): SummaryItems {
    if (node.storage) {
      return [
        {
          id: `storage`,
          title: formatMessage(messages.summaryStorage),
          description: prettySize(node.storage),
        },
      ]
    }

    return [
      { rawSize: node.hotStorage, message: messages.hotStorage },
      { rawSize: node.hotMemory, message: messages.hotMemory },
      { rawSize: node.warmStorage, message: messages.warmStorage },
      { rawSize: node.warmMemory, message: messages.warmMemory },
      { rawSize: node.coldStorage, message: messages.coldStorage },
      { rawSize: node.coldMemory, message: messages.coldMemory },
      { rawSize: node.frozenMemory * blobStorageMultiplier, message: messages.frozenStorage }, // Memory * blob storage is the display value we use for frozen storage
      { rawSize: node.frozenMemory, message: messages.frozenMemory },
    ]
      .filter(({ rawSize }) => rawSize)
      .map(({ rawSize, message }) => ({
        id: 'storage',
        title: formatMessage(message),
        description: prettySize(rawSize),
      }))
  }
}

function getTier(topologyElement): string | undefined {
  return (
    (isFrozen({ topologyElement }) && `frozen`) ||
    (isCold({ topologyElement }) && `cold`) ||
    (isWarm({ topologyElement }) && `warm`) ||
    (isHot({ topologyElement }) && `hot`) ||
    undefined
  )
}

export function sumNodeTypes(nodeConfigurations, instanceConfigurations) {
  const hasTieBreaker = hasTiebreakerMaster(nodeConfigurations, instanceConfigurations)
  const result = {}

  nodeConfigurations.forEach((nodeConfig) => {
    const instanceType = getInstanceType({ nodeConfig, instanceConfigurations })
    result[instanceType] ??= {}

    const memory = sumMemory({
      instanceConfigurations,
      nodeConfigurations: [nodeConfig],
    })

    const storage = sumStorage({
      instanceConfigurations,
      nodeConfigurations: [nodeConfig],
    })

    if (storage === 0 && memory === 0) {
      return
    }

    const isDedicated = isDedicatedNodeType(nodeConfig)

    if (isDedicated) {
      const dedicatedNodeType = getOnlyNodeType(nodeConfig)!
      result[instanceType][dedicatedNodeType] = memory
    } else {
      if (
        hasTieBreaker &&
        instanceType === `elasticsearch` &&
        isMaster({ topologyElement: nodeConfig })
      ) {
        result[instanceType].master = 1024
      }

      const tier = getTier(nodeConfig)

      if (!tier) {
        result[instanceType] = {
          ...result[instanceType],
          memory: memory + (result[instanceType].memory ?? 0),
          storage: storage + (result[instanceType].storage ?? 0),
        }
      } else {
        result[instanceType] = {
          ...result[instanceType],
          [`${tier}Storage`]: storage,
          [`${tier}Memory`]: memory,
        }
      }
    }
  })

  return result
}

function getInstanceType({ nodeConfig, instanceConfigurations }) {
  if (isDedicatedML({ topologyElement: nodeConfig })) {
    return 'ml'
  }

  const instanceConfiguration = instanceConfigurations.find(
    ({ id }) => id === nodeConfig.instance_configuration_id,
  )

  return instanceConfiguration && instanceConfiguration.instance_type
}

function getEsDataNodes({ instanceConfigurations, nodeConfigurations }) {
  return nodeConfigurations.filter((nodeConfig) => {
    const instanceType = getInstanceType({ nodeConfig, instanceConfigurations })

    if (instanceType !== `elasticsearch`) {
      return false
    }

    const isDedicated = isDedicatedNodeType(nodeConfig)
    return !isDedicated
  })
}

export function getOnlyNodeType(
  topologyElement: ElasticsearchClusterTopologyElement,
): string | undefined {
  const nodeTypes = getUnhiddenNodeRoles({ topologyElement })
  return nodeTypes.length === 1 ? nodeTypes[0] : undefined
}

function isDedicatedNodeType(nodeConfig) {
  return dedicatedNodeTypes.some((dedicatedNodeType) => {
    const onlyNodeType = getOnlyNodeType(nodeConfig)
    return onlyNodeType === dedicatedNodeType
  })
}

export function getArchitecturePricingItems({
  intl: { formatMessage },
  regionId,
  nodeConfigurations,
  instanceConfigurations,
  basePrices,
  level,
  isAutoscalingEnabled,
}: {
  intl: IntlShape
  regionId: RegionId
  nodeConfigurations: NodeConfiguration[]
  instanceConfigurations: InstanceConfiguration[]
  basePrices: BasePrice[] | undefined
  level: BillingSubscriptionLevel
  isAutoscalingEnabled?: boolean
}): SummaryItems {
  if (!basePrices) {
    return []
  }

  const { hourlyRate, tiebreakerPrice, breakdownHourlyRate } = getHourlyRate({
    regionId,
    nodeConfigurations,
    instanceConfigurations,
    basePrices,
    level,
  })

  if (hourlyRate === 0) {
    return []
  }

  return getNativePricingItems(isAutoscalingEnabled)

  function getNativePricingItems(isAutoscalingEnabled?: boolean): SummaryItems {
    return [
      getPriceDescription({ id: `total`, price: hourlyRate }, isAutoscalingEnabled),
      ...getBreakdownHourlyRate(),
    ]
  }

  function getBreakdownHourlyRate() {
    const breakdownRatesById = groupBy(breakdownHourlyRate, ({ id }) =>
      id?.startsWith(`data`) ? `data` : id,
    )
    // if we have 2 data node (hi-lo) sum price per type
    const breakdownHourlyRates = Object.keys(breakdownRatesById).reduce((breakdownRates, id) => {
      let priceSum = sumBy(breakdownRatesById[id], `price`)

      if (id.startsWith(`data`) && breakdownRatesById.master) {
        priceSum += breakdownRatesById.master[0]!.price
      }

      breakdownRates.push({ price: priceSum, id })
      return breakdownRates
    }, [] as Array<{ price: number; id: string }>)
    return breakdownHourlyRates.map((rates) => getPriceDescription(rates, isAutoscalingEnabled))
  }

  function getPriceDescription({ id, price }, isAutoscalingEnabled) {
    if (price === 0) {
      return {
        id,
        title: isAutoscalingEnabled
          ? formatMessage(messages.initialHourlyRate)
          : formatMessage(messages.hourlyRate),
        description: <Fragment>{formatMessage(messages.free)}</Fragment>,
      }
    }

    const money = formatCenticent(price, 4)

    return {
      descriptionClass: 'hourly-price',
      id,
      title: isAutoscalingEnabled
        ? formatMessage(messages.initialHourlyRate)
        : formatMessage(messages.hourlyRate),
      ...(id?.startsWith(`data`) && tiebreakerPrice
        ? { tiebreakerPrice: formatCenticent(tiebreakerPrice.price, 4) }
        : {}),
      description: <Fragment>{money}</Fragment>,
    }
  }
}

export function getHourlyRate({
  basePrices,
  regionId,
  instanceConfigurations,
  nodeConfigurations,
  level,
}: {
  basePrices: BasePrice[]
  regionId: RegionId
  instanceConfigurations: InstanceConfiguration[]
  nodeConfigurations: Array<ElasticsearchClusterTopologyElement | AppSearchTopologyElement>
  level: BillingSubscriptionLevel
}) {
  const isProduction = getConfigForKey(`NODE_ENV`) === `production`
  const activeConfigurations = nodeConfigurations.filter(whereNodeConfigurationIsValid)

  const breakdown = activeConfigurations
    .map(getNodeConfigurationPrice)
    .filter((item): item is ArchitectureBreakdownItem => item != null)

  const tiebreakerPrice: ArchitectureBreakdownItem | null = getTiebreakerHourlyRate()

  if (tiebreakerPrice != null && tiebreakerPrice.instanceConfiguration != null) {
    breakdown.push(tiebreakerPrice)
  }

  const hourlyRate = breakdown.reduce((sum, item) => sum + item.price, 0)

  const breakdownHourlyRate = breakdown.map((item) => {
    const instanceType = item.instanceConfiguration?.instance_type

    if (!instanceType) {
      throw new Error(
        `Could not find instance_type in instance configuration [${item.instanceConfiguration?.id}]`,
      )
    }

    const type = deriveNodeType(item.nodeConfiguration, instanceType)

    return {
      id: type === `ingest` ? `data` : type,
      price: item.price,
    }
  })

  return {
    breakdown,
    hourlyRate,
    breakdownHourlyRate,
    tiebreakerPrice,
  }

  function whereNodeConfigurationIsValid(nodeConfiguration) {
    const enabled = isEnabledConfiguration(nodeConfiguration)

    if (enabled === false) {
      return false
    }

    const { instance_configuration_id: id } = nodeConfiguration
    const instanceConfiguration = instanceConfigurations.find((each) => each.id === id)

    return instanceConfiguration != null // sanity
  }

  function getNodeConfigurationPrice(
    nodeConfiguration: ElasticsearchClusterTopologyElement,
  ): ArchitectureBreakdownItem | null {
    const { instance_configuration_id: id } = nodeConfiguration
    const instanceConfiguration = instanceConfigurations.find((each) => each.id === id)

    if (!instanceConfiguration) {
      return null
    }

    return getPrice({
      regionId,
      nodeConfiguration,
      instanceConfiguration,
      basePrices,
      level,
    })
  }

  function getTiebreakerHourlyRate(): ArchitectureBreakdownItem | null {
    const hasDedicatedMaster = activeConfigurations.some((topologyElement) =>
      isDedicatedMaster({ topologyElement }),
    )

    if (hasDedicatedMaster) {
      return null
    }

    const hasTieBreaker = hasTiebreakerMaster(nodeConfigurations, instanceConfigurations)

    if (!hasTieBreaker) {
      return null
    }

    const tieBreakerInstanceConfiguration = getTiebreakerMaster(instanceConfigurations)

    if (!tieBreakerInstanceConfiguration) {
      return {
        sku: null,
        price: 0, // sanity
        pricePointFail: true,
        instanceConfiguration: null,
        nodeConfiguration: null,
      }
    }

    const dedicatedMasterBasePrice = basePrices.find((price) =>
      matchDedicatedMasterBasePrice({
        price,
        userLevel: level,
        id:
          (tieBreakerInstanceConfiguration.metadata?.sku as string) ||
          tieBreakerInstanceConfiguration.id,
      }),
    )

    if (dedicatedMasterBasePrice == null) {
      if (!isProduction) {
        console.error(`Did not find a base price for "dedicated master".`)
      }

      return {
        sku: null,
        price: 0, // sanity
        pricePointFail: true,
        instanceConfiguration: null,
        nodeConfiguration: null,
      }
    }

    const { sku } = dedicatedMasterBasePrice

    const instanceConfiguration = instanceConfigurations.find((config) => {
      if (config.metadata?.sku === sku) {
        return true
      }

      return config.id === sku
    })

    if (instanceConfiguration == null) {
      return {
        sku: null,
        price: 0, // sanity
        pricePointFail: true,
        instanceConfiguration: null,
        nodeConfiguration: null,
      }
    }

    // we explicitly want to add a surcharge for the 1 GB 1 AZ tiebreaker,
    // priced as a dedicated master
    const nodeConfiguration: ElasticsearchClusterTopologyElement = {
      instance_configuration_id: instanceConfiguration.id,
      node_type: { data: false, master: true, ingest: false },
      zone_count: 1,
      size: {
        resource: `memory`,
        value: 1024,
      },
    }

    const fullSku = getFullSku({
      regionId,
      sku: dedicatedMasterBasePrice.sku,
      userLevel: level,
      capacity: 1024,
      zones: 1,
    })

    return {
      sku: fullSku,
      price: dedicatedMasterBasePrice.price,
      instanceConfiguration,
      nodeConfiguration,
    }
  }
}

export function getPrice({
  basePrices,
  instanceConfiguration,
  nodeConfiguration,
  regionId,
  level,
}: {
  basePrices: BasePrice[]
  instanceConfiguration: InstanceConfiguration
  nodeConfiguration: NodeConfiguration
  regionId: RegionId
  level: BillingSubscriptionLevel
}): ArchitectureBreakdownItem | null {
  const {
    size,
    memory_per_node,
    node_count_per_zone,
    zone_count = 1,
    autoscaling_min,
  } = nodeConfiguration as ElasticsearchClusterTopologyElement

  // the SKU for an instance configuration might be different from its ID
  // this is so we can flexibly determine how an instance configuration
  // should be priced, regardless of its ID
  const { id, metadata } = instanceConfiguration
  const sku = (metadata?.sku ?? id) as string

  let memorySize = getSize({
    resource: `memory`,
    size,
    exactSize: memory_per_node,
    exactInstanceCount: node_count_per_zone,
    instanceConfiguration,
  })

  // If the size object isn't filled out for ML it means that no node has been started yet. In this case, we should use the autoscaling_min
  // to determine what the cost would be if that size node is indeed fired up
  // We're using the `size` object instead of `memorySize` here because `getSize()` already fills in a default value so it's generally
  // always filled out.
  if ((!size || size.value === 0) && isDedicatedML({ topologyElement: nodeConfiguration })) {
    memorySize = autoscaling_min?.value || 0
  }

  const memorySizeAcrossZones = memorySize * zone_count
  const memorySizeAcrossZonesInGb = memorySizeAcrossZones / 1024
  const fullSku = getFullSku({
    sku,
    regionId,
    userLevel: level,
    capacity: memorySize,
    zones: zone_count,
  })

  /* [1] free tier filters needs to match the current configuration in order
   *     for us to consider the configuration to be free.
   *
   * [2] when we fail to match free tier prices to the current configuration,
   *     we need to ensure we don't inadvertently match against an empty price
   *     meant to be matched only for the free tier.
   *
   * Example:
   * - We have a 2048 MB, 1 zone configuration of "aws.data.m4"
   * - Prices include:
   *   {
   *     "sku": "aws.data.m4",
   *     "free_tier": {
   *       "memory_capacity": 512,
   *       "zone_count": 1
   *     }
   *   }
   *
   * - If we didn't explictly match `free_tier` against `false` in [2],
   *   we'd be at risk of applying a free tier discount, even when the user went
   *   for a configuration that isn't considered free (2GB),
   *   because we'd match the 512MB free tier.
   */
  const freeTier = basePrices.find((each) => {
    if (each.sku !== sku) {
      return false
    }

    if (typeof each.free_tier !== 'object') {
      return false
    }

    return each.free_tier.memory_capacity === memorySize && each.free_tier.zone_count === zone_count
  })

  if (freeTier) {
    return {
      sku: fullSku,
      price: 0, // instance configuration settings match free tier
      instanceConfiguration,
      nodeConfiguration,
    }
  }

  const basePrice = basePrices.find(
    // [2]
    (each) => each.sku === sku && each.level === level && each.free_tier === false,
  )

  if (basePrice == null) {
    return {
      sku: fullSku,
      price: 0, // sanity
      pricePointFail: true,
      instanceConfiguration,
      nodeConfiguration,
    }
  }

  const { price } = basePrice

  return {
    sku: fullSku,
    price: price * memorySizeAcrossZonesInGb,
    instanceConfiguration,
    nodeConfiguration,
  }
}

export function matchDedicatedMasterBasePrice({
  price,
  userLevel,
  id,
}: {
  price: BasePrice
  userLevel: string
  id: string | undefined
}): boolean {
  const { sku, level, free_tier } = price

  return (
    sku === id &&
    level === userLevel &&
    (free_tier === false || (free_tier.memory_capacity === 1024 && free_tier.zone_count === 1))
  )
}

function sumMemory({
  instanceConfigurations,
  nodeConfigurations,
}: {
  instanceConfigurations: InstanceConfiguration[]
  nodeConfigurations: NodeConfiguration[]
}): number {
  return sumBy(nodeConfigurations, (nodeConfiguration) => {
    const {
      instance_configuration_id,
      size,
      memory_per_node,
      node_count_per_zone,
      zone_count = 1,
    } = nodeConfiguration as ElasticsearchClusterTopologyElement

    const enabled = isEnabledConfiguration(nodeConfiguration)

    if (!enabled) {
      return 0
    }

    const instanceConfiguration = instanceConfigurations.find(
      (each) => each.id === instance_configuration_id,
    )

    if (!instanceConfiguration) {
      return 0
    }

    const memorySize = getSize({
      resource: `memory`,
      size,
      exactSize: memory_per_node,
      exactInstanceCount: node_count_per_zone,
      instanceConfiguration,
    })
    return memorySize * zone_count
  })
}

function sumSystemAddedMemory({
  instanceConfigurations,
  nodeConfigurations,
}: {
  instanceConfigurations: InstanceConfiguration[]
  nodeConfigurations: NodeConfiguration[]
}): number {
  const hasTiebreaker = hasTiebreakerMaster(nodeConfigurations, instanceConfigurations)

  const tiebreakerMemory = hasTiebreaker ? 1024 : 0
  return tiebreakerMemory
}

function sumStorage({
  instanceConfigurations,
  nodeConfigurations,
}: {
  instanceConfigurations: InstanceConfiguration[]
  nodeConfigurations: NodeConfiguration[]
}): number {
  return sumBy(nodeConfigurations, (nodeConfiguration) => {
    const {
      instance_configuration_id,
      size,
      memory_per_node,
      node_count_per_zone,
      zone_count = 1,
    } = nodeConfiguration as ElasticsearchClusterTopologyElement

    const enabled = isEnabledConfiguration(nodeConfiguration)

    if (!enabled) {
      return 0
    }

    const instanceConfiguration = instanceConfigurations.find(
      (each) => each.id === instance_configuration_id,
    )

    if (!instanceConfiguration) {
      return 0
    }

    if (isFrozen({ topologyElement: nodeConfiguration })) {
      const blobStorageSize = getNumber({
        resourceIn: `memory`,
        storageMultiplier: instanceConfiguration.storage_multiplier,
        totalSize: size!.value,
        resourceOut: `storage`,
        isBlobStorage: true,
      })

      return blobStorageSize * zone_count
    }

    const storageSize = getSize({
      resource: `storage`,
      size,
      exactSize: memory_per_node,
      exactInstanceCount: node_count_per_zone,
      instanceConfiguration,
    })

    return storageSize * zone_count
  })
}

export function getMaxZoneCount({
  nodeConfigurations,
}: {
  nodeConfigurations: NodeConfiguration[]
}): number {
  const enabledConfigurations = nodeConfigurations.filter((configuration) =>
    isEnabledConfiguration(configuration),
  )
  const maxConfiguration = maxBy(enabledConfigurations, `zone_count`)
  return maxConfiguration?.zone_count ?? 0
}

/**
 * Determines whether a topology element can be disabled.
 *
 * @param {'elasticsearch' | 'kibana' | 'apm'} instanceType
 * @param {ElasticsearchClusterTopologyElement | KibanaClusterTopologyElement | ApmTopologyElement} nodeConfiguration
 * @return {boolean}
 */
export function canDisableConfiguration({
  topologyElement,
  sliderInstanceType,
  dedicatedMasterThreshold,
}: {
  topologyElement: AnyTopologyElement
  sliderInstanceType?: SliderInstanceType
  dedicatedMasterThreshold?: number
}): boolean {
  // SPECIAL CASES

  // a dedicated master threshold trumps user choice

  if (dedicatedMasterThreshold != null && isDedicatedMaster({ topologyElement })) {
    return false
  }

  // GENERIC CASES

  // use the first-class indicator if present

  if (sliderInstanceType === `elasticsearch`) {
    if ((topologyElement as ElasticsearchClusterTopologyElement).topology_element_control) {
      return (
        (topologyElement as ElasticsearchClusterTopologyElement).topology_element_control?.min
          .value === 0
      )
    }
  }

  // else fall back to pre-topology_element_control hard coded approach

  if (sliderInstanceType === `elasticsearch`) {
    const activeNodeTypes = getNodeRoles({ topologyElement })

    if (activeNodeTypes.length === 1 && [`ml`, `ingest`].includes(activeNodeTypes[0]!)) {
      return true
    }
  }

  if (sliderInstanceType === `apm`) {
    return true
  }

  if (sliderInstanceType === `integrations_server`) {
    return true
  }

  if (sliderInstanceType === `enterprise_search`) {
    return true
  }

  return false
}

// Mirrors accounter code at https://github.com/elastic/cloud/blob/275f2c674136159b353d8f4990717941339e5222/python-services/found_services/accounter/accounter_service.py#L483
export function getFullSku({
  sku: base_sku,
  regionId: region,
  userLevel,
  capacity,
  zones: azs,
}: {
  sku: string
  regionId: string
  userLevel: 'enterprise' | 'platinum' | 'gold' | 'standard'
  capacity: number
  zones: number
}) {
  const prefix = getSkuPrefix(userLevel)
  const fullSku = `${prefix}${base_sku}_${region}_${capacity}_${azs}`

  return fullSku
}

function getSkuPrefix(userLevel: string): string {
  if (userLevel !== `standard`) {
    return `Cloud-${capitalize(userLevel)}_`
  }

  return ``
}

export function getInstancesByType(
  architecture: ZoneSummaryKey | undefined,
  sliderInstanceType?: SliderInstanceType,
): ZoneSummaryInstance[] {
  return (
    architecture
      ?.filter(([instance]) => instance.sliderInstanceType === sliderInstanceType)
      .map(([instance]) => instance) || []
  )
}

export function findInstanceByExactMatch(
  instances: ZoneSummaryInstance[],
  instance: ZoneSummaryInstance,
): ZoneSummaryInstance | undefined {
  return instances.find((newInstance) => isEqual(newInstance, instance))
}

export function findInstanceByPartialMatch(
  instances: ZoneSummaryInstance[],
  instance: ZoneSummaryInstance,
): ZoneSummaryInstance | undefined {
  const instancesMatchingSliderInstanceType = instances.filter(
    ({ sliderInstanceType }) => instance.sliderInstanceType === sliderInstanceType,
  )
  const instanceSingleSliderInstanceTypeMatch =
    instancesMatchingSliderInstanceType.length === 1
      ? instancesMatchingSliderInstanceType[0]
      : undefined
  const instanceNodeTypesMatch = findInstanceByNodeTypes(instances, instance)
  const instanceMatchingId = instances.find(({ id }) => id === instance.id)

  const [instancePartialMatch] = [
    instanceSingleSliderInstanceTypeMatch,
    instanceNodeTypesMatch,
    instanceMatchingId,
  ].filter(Boolean)

  return instancePartialMatch
}

export function isMatchingInstance(instance1: ZoneSummaryInstance, instance2: ZoneSummaryInstance) {
  return [findInstanceByExactMatch, findInstanceByPartialMatch].some((fn) =>
    fn([instance1], instance2),
  )
}

export function findInstanceByNodeTypes(
  instances: ZoneSummaryInstance[],
  instance: ZoneSummaryInstance,
): ZoneSummaryInstance | undefined {
  const nodeTypeMatcher = ({ sliderNodeType }: ZoneSummaryInstance) =>
    intersection(sliderNodeType, instance.sliderNodeType).length

  const instanceNodeTypesMatches = instances.filter(nodeTypeMatcher)

  if (instance.sliderNodeType?.length === 1 && instance.sliderNodeType[0] === `ingest`) {
    // if we're looking at a dedicated ingest node, then try to find an equivalent dedicated ingest node
    // by finding an instance that has a single ingest value for it's node type
    const onlyType = instanceNodeTypesMatches.find((i) => i.sliderNodeType?.length === 1)

    if (onlyType) {
      return onlyType
    }
  }

  const [closestMatch] = sortBy(instanceNodeTypesMatches)

  return closestMatch
}
